import { useState, useEffect } from 'react';

import { Download } from 'lucide-react';

import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { BackButton } from '@/components/atoms/BackButton';
import { DownloadTableModal } from '@/components/molecules/DownloadTable';
import { DownloadWrittenModal } from '@/components/molecules/DownloadWritten';
import Modal from '@/components/molecules/Modals/SettingsModal';
import DownloadFormatSelector from '@/screens/Chronos/CaseEditor/DataView/components/DownloadFormatSelector';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalTitle: string;
  downloadTitle: string;
  isLoading: boolean;
  columnOptions: { label: string; value: string }[];
  onDownloadTable: (params: {
    title: string;
    columns: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
  }) => void;
  onDownloadWritten: (params: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
    includeSourceDocuments?: boolean;
  }) => void;
  displayOptions?: {
    showStyle?: boolean;
    showDocumentOptions?: boolean;
    showSourceDocumentsOption?: boolean;
  };
}

export const DownloadModal = ({
  isOpen,
  onClose,
  modalTitle,
  downloadTitle,
  isLoading,
  columnOptions,
  onDownloadTable,
  onDownloadWritten,
  displayOptions = {
    showStyle: true,
    showDocumentOptions: false,
    showSourceDocumentsOption: false,
  },
}: DownloadModalProps) => {
  const [selectedFormat, setSelectedFormat] = useState<StyleFormat | null>(null);

  const handleClose = () => {
    onClose();
    // Reset format after modal close animation
    setTimeout(() => setSelectedFormat(null), 300);
  };

  const handleDownloadTableWithClose = (params: {
    title: string;
    columns: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
  }) => {
    onDownloadTable(params);
  };

  const handleDownloadWrittenWithClose = (params: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
    includeSourceDocuments?: boolean;
  }) => {
    onDownloadWritten(params);
  };

  // Reset format when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-4 font-semibold text-gray-700">
          {selectedFormat === null ? (
            <>
              <Download />
              {modalTitle}
            </>
          ) : (
            <>
              <BackButton onClick={() => setSelectedFormat(null)} />
              {modalTitle} - {selectedFormat === StyleFormat.Table ? 'Table' : 'Written'}
            </>
          )}
        </div>
      }
      isOpen={isOpen}
      handleClose={handleClose}
      content={
        selectedFormat === null ? (
          <DownloadFormatSelector onSelectFormat={setSelectedFormat} />
        ) : selectedFormat === StyleFormat.Table ? (
          <DownloadTableModal
            columnOptions={columnOptions}
            downloadTitle={downloadTitle}
            loadingDownload={isLoading}
            handleDownload={handleDownloadTableWithClose}
            showSourceDocumentsOption={displayOptions.showSourceDocumentsOption}
          />
        ) : (
          <DownloadWrittenModal
            downloadTitle={downloadTitle}
            loadingDownload={isLoading}
            handleDownload={handleDownloadWrittenWithClose}
            showStyle={displayOptions.showStyle}
            showDocumentOptions={displayOptions.showDocumentOptions}
            showSourceDocumentsOption={displayOptions.showSourceDocumentsOption}
          />
        )
      }
      size="small"
    />
  );
};
